import { useAuth } from '@/modules/auth/hooks/use-auth';
import { useUser } from '@/modules/auth/hooks/use-user';

import type { ProfileCompletion, ProfileReward } from '../types';
import { isProfileNudgeSnoozed } from '../utils/nudge';
import { useProfileCompletion } from './use-profile-completion';

/** "cupom de R$ 10", "cupom de 15%", "150 pontos". */
export function shortRewardLabel(r: ProfileReward): string {
  if (r.kind === 'points') return `${r.points ?? 0} pontos`;
  const value = Number(r.discount ?? 0);
  if (r.discount_type === 'PERCENTAGE') return `cupom de ${value.toLocaleString('pt-BR')}%`;
  const reais = value.toLocaleString('pt-BR', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `cupom de R$ ${reais}`;
}

/** Ainda falta algo no perfil (ou confirmar o e-mail para ganhar o presente). */
export function profileNeedsAttention(data: ProfileCompletion): boolean {
  const needsConfirm =
    data.rewards.length > 0 &&
    data.requirements.includes('email') &&
    !data.email.is_generic &&
    !data.email.verified &&
    !data.email.pending;
  // A data de nascimento é opcional: entra só na barra do Meu perfil, sem ponto nem selo.
  return !data.items.email || !data.items.cpf || needsConfirm;
}

/**
 * PRAT-161: lembretes discretos de completar o perfil fora do Meu perfil —
 * ponto no ícone Perfil e botão discreto no topo da página da loja.
 * Respeitam o "Agora não" (30 dias) e somem com o perfil completo.
 */
export function useProfileNudge(slug?: string) {
  const { isAuthenticated } = useAuth();
  const { user } = useUser();
  const snoozed = isProfileNudgeSnoozed(user.get()?.id);
  const { data } = useProfileCompletion(slug || undefined, isAuthenticated && !snoozed);

  const active = !!data && isAuthenticated && !snoozed && profileNeedsAttention(data);
  const rewards = active ? data!.rewards : [];

  return {
    showDot: active,
    /** % do perfil (anel do botão no topo da loja). */
    percent: active ? data!.percent : 100,
    /** Presente mais curto para caber no topo: "cupom de R$ 10", "150 pontos". */
    rewardShort: rewards.length ? shortRewardLabel(rewards[0]) : null,
  };
}
